import { Input, Select, Space, Tabs } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { useCharacterScope } from "../../services/character";
import { fmtNumber, RelativeTime, StatusTag } from "../../components/format";

const FILTERS = ["search", "status", "sort", "order"];

/** 会话 / 全文检索 / 反馈三个页签共用一个路由前缀。 */
export function ConversationTabs({ active }: { active: string }) {
  const navigate = useNavigate();
  return (
    <Tabs
      activeKey={active}
      onChange={(k) => navigate(k)}
      items={[
        { key: "/conversations", label: "会话列表" },
        { key: "/conversations/search", label: "消息全文检索" },
        { key: "/conversations/feedback", label: "用户反馈" },
      ]}
    />
  );
}

interface ConversationRow {
  id: string;
  relationshipId: string;
  userId: string;
  userName: string | null;
  product: string;
  status: string;
  messageCount: number;
  lastSequence: number;
  memoryTitle: string | null;
  memorySummary: string | null;
  affinityDelta: number | null;
  lastMessageAt: string | null;
}

export function ConversationsPage() {
  const scope = useCharacterScope();
  const { searchParams, setParam } = useTableParams(FILTERS);
  const location = useLocation();

  return (
    <>
      <ConversationTabs active={location.pathname} />
      <DataTable<ConversationRow>
        path="/api/v1/admin/conversations"
        fixedParams={scope}
        filterKeys={FILTERS}
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              style={{ width: 300 }}
              placeholder="会话标题 / 用户昵称 / 会话 ID"
              defaultValue={searchParams.get("search") ?? ""}
              onSearch={(v) => setParam("search", v)}
            />
            <Select
              allowClear
              style={{ width: 130 }}
              placeholder="状态"
              value={searchParams.get("status") ?? undefined}
              onChange={(v) => setParam("status", v)}
              options={[
                { value: "active", label: "active" },
                { value: "archived", label: "archived" },
              ]}
            />
            <Select
              style={{ width: 160 }}
              value={searchParams.get("sort") ?? "lastMessageAt"}
              onChange={(v) => setParam("sort", v)}
              options={[
                { value: "lastMessageAt", label: "按最后消息" },
                { value: "createdAt", label: "按创建时间" },
                { value: "lastSequence", label: "按消息条数" },
              ]}
            />
          </Space>
        }
        columns={[
          {
            title: "会话",
            dataIndex: "id",
            width: 90,
            render: (v: string) => <Link to={`/conversations/${v}`}>{v.slice(-8)}</Link>,
          },
          {
            title: "用户",
            dataIndex: "userName",
            width: 140,
            render: (v: string | null, r) => <Link to={`/users/${r.userId}`}>{v || "(未命名)"}</Link>,
          },
          {
            title: "标题",
            dataIndex: "memoryTitle",
            width: 220,
            // 会话标题来自最后一段 ConversationMemory。尚未总结的会话没有标题。
            render: (v) => v || <span style={{ color: "#999" }}>未总结</span>,
          },
          { title: "摘要", dataIndex: "memorySummary", render: (v) => v || "—" },
          { title: "入口", dataIndex: "product", width: 110 },
          { title: "消息", dataIndex: "messageCount", width: 80, align: "right", render: fmtNumber },
          {
            title: "好感变化",
            dataIndex: "affinityDelta",
            width: 100,
            align: "right",
            render: (v: number | null) =>
              v === null ? (
                "—"
              ) : (
                <span style={{ color: v > 0 ? "#2F7A4B" : v < 0 ? "#B4232E" : undefined }}>
                  {v > 0 ? `+${v}` : v}
                </span>
              ),
          },
          { title: "状态", dataIndex: "status", width: 90, render: (v) => <StatusTag value={v} /> },
          {
            title: "最后消息",
            dataIndex: "lastMessageAt",
            width: 130,
            render: (v) => <RelativeTime value={v} />,
          },
          {
            title: "关系",
            dataIndex: "relationshipId",
            width: 90,
            render: (v: string) => <Link to={`/users/${v}`}>{v.slice(-8)}</Link>,
          },
        ]}
      />
    </>
  );
}
